import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Alert,
} from '@mui/material';
import { Close as CloseIcon, Refresh as RefreshIcon, OpenInNew as OpenInNewIcon } from '@mui/icons-material';
import { Planos } from '../../types';
import { planosService, PlanoServicoVinculado } from '../../services/planosService';
import { getStatusColor, getStatusLabel } from '../../utils/contractStatusUtils';

interface PlanosServicosModalProps {
  open: boolean;
  onClose: () => void;
  plano: Planos | null;
  filtro: 'ativos' | 'cancelados';
}

const TIPO_LABELS: Record<string, string> = {
  internet: 'Internet',
  telefonia: 'Telefonia',
  tv: 'TV',
};

const PlanosServicosModal: React.FC<PlanosServicosModalProps> = ({
  open,
  onClose,
  plano,
  filtro,
}) => {
  const navigate = useNavigate();
  const [servicos, setServicos] = useState<PlanoServicoVinculado[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const tituloFiltro = filtro === 'cancelados' ? 'Cancelados' : 'Ativos';

  const carregarServicos = useCallback(async () => {
    if (!plano) return;
    setLoading(true);
    setError(null);
    try {
      const response = await planosService.getServicosByPlano(plano.id, filtro);
      setServicos(response.data || []);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Erro ao carregar serviços');
      setServicos([]);
    } finally {
      setLoading(false);
    }
  }, [plano, filtro]);

  useEffect(() => {
    if (open && plano) {
      carregarServicos();
    }
  }, [open, plano, carregarServicos]);

  const handleClose = () => {
    setServicos([]);
    setError(null);
    onClose();
  };

  const handleOpenContrato = (contratoId: number) => {
    navigate(`/contracts/${contratoId}`);
    handleClose();
  };

  const handleOpenCliente = (clienteId: number) => {
    navigate(`/clients/${clienteId}`);
    handleClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ p: 1.5 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box minWidth={0}>
            <Typography variant="h6" sx={{ fontSize: '1.05rem', fontWeight: 700 }} noWrap>
              Serviços {tituloFiltro} — {plano?.nome}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Contratos vinculados a este plano
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={0.5}>
            <IconButton onClick={carregarServicos} disabled={loading} size="small" title="Atualizar">
              <RefreshIcon fontSize="small" />
            </IconButton>
            <IconButton onClick={handleClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 1.5, pt: 0 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 1.5, borderRadius: 1.5 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box display="flex" flexDirection="column" alignItems="center" py={4} gap={1}>
            <CircularProgress size={28} />
            <Typography variant="caption" color="text.secondary">
              Carregando serviços...
            </Typography>
          </Box>
        ) : servicos.length === 0 ? (
          <Box textAlign="center" py={4}>
            <Typography variant="body2" color="text.secondary">
              Nenhum serviço {tituloFiltro.toLowerCase()} vinculado a este plano.
            </Typography>
          </Box>
        ) : (
          <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem' }}>ID</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem' }}>Cliente</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem' }}>Contrato</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem' }}>Tipo</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem' }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem' }}>PPPoE</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem' }}>Endereço</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.72rem', width: 48 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {servicos.map((servico) => {
                  const cliente = servico.contrato?.cliente;
                  const endereco = [servico.endereco_inst, servico.cidade_inst].filter(Boolean).join(' — ');
                  return (
                    <TableRow key={servico.id} hover>
                      <TableCell sx={{ fontSize: '0.78rem', fontWeight: 600, color: 'primary.main' }}>
                        #{servico.id}
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.78rem' }}>
                        {cliente ? (
                          <Typography
                            component="span"
                            variant="body2"
                            sx={{
                              fontSize: '0.78rem',
                              fontWeight: 600,
                              color: 'primary.main',
                              cursor: 'pointer',
                              '&:hover': { textDecoration: 'underline' },
                            }}
                            onClick={() => handleOpenCliente(cliente.id)}
                          >
                            {cliente.nome}
                          </Typography>
                        ) : (
                          '—'
                        )}
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.78rem' }}>
                        {servico.contrato ? (
                          <Typography
                            component="span"
                            variant="body2"
                            sx={{
                              fontSize: '0.78rem',
                              cursor: 'pointer',
                              color: 'text.primary',
                              '&:hover': { color: 'primary.main', textDecoration: 'underline' },
                            }}
                            onClick={() => handleOpenContrato(servico.contrato!.id)}
                          >
                            {servico.contrato.titulo || `#${servico.contrato_id}`}
                          </Typography>
                        ) : (
                          `#${servico.contrato_id}`
                        )}
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.78rem' }}>
                        {TIPO_LABELS[servico.tipo] || servico.tipo}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getStatusLabel(servico.status)}
                          size="small"
                          color={getStatusColor(servico.status)}
                          sx={{ height: 20, fontSize: '0.65rem', fontWeight: 600 }}
                        />
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.75rem', fontFamily: 'monospace' }}>
                        {servico.usuario_pppoe || '—'}
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.75rem', maxWidth: 180 }} title={endereco}>
                        <Typography variant="body2" noWrap sx={{ fontSize: '0.75rem' }}>
                          {endereco || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          title="Abrir contrato"
                          onClick={() => handleOpenContrato(servico.contrato_id)}
                        >
                          <OpenInNewIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2, pb: 2 }}>
        <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
          {servicos.length} serviço{servicos.length !== 1 ? 's' : ''} {tituloFiltro.toLowerCase()}
        </Typography>
        <Button onClick={handleClose} size="small">
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PlanosServicosModal;
